<template>
  <div class="HowItWorks">
    <div class="HowItWorks-inner">
      <header class="HowItWorks-header">
        <Eyebrow v-if="eyebrow" :label="eyebrow" />

        <h2 class="HowItWorks-title">
          {{ title }}
        </h2>

        <p v-if="subtitle" class="HowItWorks-subtitle">
          {{ subtitle }}
        </p>
      </header>

      <ScrollCarousel :items="steps" tag="ol" :ariaLabel="title">
        <li
          v-for="(step, index) of steps"
          :key="step.title"
          class="HowItWorks-step"
        >
          <StepCard
            v-bind="step"
            :stepNumber="stepNumber(index)"
            :label="stepLabel(index)"
          />
        </li>
      </ScrollCarousel>

      <Callout v-if="callout" :text="callout" />

      <SubscriptionCycle
        v-if="cycleSteps?.length"
        :title="cycleTitle"
        :steps="cycleSteps"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from '@cvo/i18n/composables/useI18n';
import Callout from '@cvo/ui/components/atoms/Callout/Callout.vue';
import Eyebrow from '@cvo/ui/components/atoms/Eyebrow/Eyebrow.vue';
import StepCard from '@cvo/ui/components/molecules/StepCard/StepCard.vue';
import ScrollCarousel from '@cvo/ui/components/molecules/ScrollCarousel/ScrollCarousel.vue';
import SubscriptionCycle from '@cvo/ui/components/molecules/SubscriptionCycle/SubscriptionCycle.vue';
import type { HowItWorksProps } from './HowItWorks.model';

const props = defineProps<HowItWorksProps>();

const STEP_LABEL_KEY = 'custom.landing.howItWorks.stepLabel';
const STEP_NUMBER_LENGTH = 2;

const { t, te } = useI18n();

/** Formats the displayed step figure, e.g. "01" for the first step. */
function stepNumber(index: number): string {
  return String(index + 1).padStart(STEP_NUMBER_LENGTH, '0');
}

/** Formats the "STEP 01" label. Returns undefined when the tenant defines no template. */
function stepLabel(index: number): string | undefined {
  if (!te(STEP_LABEL_KEY)) return undefined;

  return t(STEP_LABEL_KEY, { number: stepNumber(index) });
}
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/responsive';
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  .HowItWorks {
    &-inner {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-6;

      @include responsive.desktop {
        gap: vars.$dimensions-d-12;
      }
    }

    &-header {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: vars.$dimensions-d-2;
      text-align: center;

      @include responsive.tablet {
        gap: vars.$dimensions-d-4;
      }
    }

    &-title {
      margin: 0;
      color: vars.$patterns-how-it-works-title-color;
      // Adds up with the header gap to leave 16px below the eyebrow
      margin-block-start: vars.$dimensions-d-2;

      @include responsive.tablet {
        margin-block-start: vars.$dimensions-d-0;
      }

      @include text.heading-responsive('h4', 'h1', 'h1');
    }

    &-subtitle {
      margin: 0;
      color: vars.$patterns-how-it-works-subtitle-color;

      @include text.text-responsive('md', 'lg', 'lg');
    }

    &-step {
      flex: 0 0 100%;
      scroll-snap-align: start;

      @include responsive.desktop {
        flex: 0 1 376px;
      }

      > * {
        height: 100%;
      }
    }
  }

  // Connector running behind the step cards, visible only in the gaps between them.
  // `inset-block: 0` + `margin-block: auto` centres it on the cards, which
  // stretch to the track height. Drawn as a border so it renders dotted.
  .HowItWorks .ScrollCarousel-track {
    &::before {
      @include responsive.desktop {
        content: '';
        position: absolute;
        inset-inline: 0;
        inset-block: 0;
        block-size: vars.$dimensions-d-0;
        margin-block: auto;
        border-block-start: vars.$dimensions-d-1 solid vars.$organisms-steps-connector-color;
      }
    }
  }
}
</style>
